<template>
  <v-app id="inspire">
    <v-app-bar
      app
      dark 
      color="#E4ECE2"
      elevation="2"
      height="84"
    >
      <v-toolbar-title class="headline" style="display: flex; align-items: center;">
        <img :src="logoSrc" alt="Logo" class="logo-src" style="margin-right: 16px;" />
      </v-toolbar-title>

      <!-- Spacer -->
      <v-spacer></v-spacer>

      <!-- Theme Toggle -->
      <v-btn icon @click="toggleTheme" class="theme-change">
        <svg-icon type="mdi" :path="mdiThemeLightDark"></svg-icon>
      </v-btn>

      <!-- Avatar -->
      <v-tooltip text="Account" location="top">
        <template v-slot:activator="{ props }">
          <v-avatar 
            v-bind="props" 
            color="#1F3A3B" 
            size="36px" 
            @click="navigateToSettings"
            style="cursor: pointer;"
          >
            {{ userInitials }}  
          </v-avatar>
        </template>
      </v-tooltip>

      <!-- Logout Button -->
      <v-tooltip text="Logout" location="top">
        <template v-slot:activator="{ props }">
          <v-btn v-bind="props" icon @click="handleLogout">
            <v-icon>mdi-logout</v-icon>
          </v-btn>
        </template>
      </v-tooltip>
    </v-app-bar>

    <v-navigation-drawer
      v-model="drawer"
      app
      permanent
      class="drawer"
    >
      <v-list-item>
        <v-list-item-title class="title text-left">
          <a href="/" style="font-size: 24px; text-decoration: none; color: #555; margin-top: 10px;">{{ apptitle }}</a>
          <p style="font-size: 16px; color: #888;">{{ companyName }}</p>
        </v-list-item-title>
      </v-list-item>

      <v-list dense nav>
        <v-list-item @click="navigateTo('/')">
          <template v-slot:prepend>
            <svg-icon type="mdi" :path="mdiViewDashboard"></svg-icon>
          </template>
          <v-list-item-title class="ml-2">{{ t('dashboard') }}</v-list-item-title>
        </v-list-item>

        <v-divider></v-divider>

        <h5 class="mt-2" style="font-weight: 300;">{{ t('warehouseManagement') }}</h5>

        <v-list-item v-for="item in items" :key="item.title" @click="navigateTo(item.to)">
          <template v-slot:prepend>
            <svg-icon type="mdi" :path="item.icon"></svg-icon>
          </template>
          <v-list-item-title class="ml-2">{{ item.title }}</v-list-item-title>
        </v-list-item>


    <!--    
        <v-divider></v-divider>
        <h5 class="mt-2" style="font-weight: 300;">{{ t('trade') }}</h5>

        <v-list-item v-for="inventoryItem in inventoryItems" :key="inventoryItem.title" @click="navigateTo(inventoryItem.to)">
          <template v-slot:prepend>
            <svg-icon type="mdi" :path="inventoryItem.icon"></svg-icon>
          </template>
          <v-list-item-title class="ml-2">{{ inventoryItem.title }}</v-list-item-title>
        </v-list-item>
    -->

        <v-divider></v-divider>
        <h5 class="mt-2" style="font-weight: 300;">{{ t('regulations') }}</h5>

        <v-list-item v-for="regulationItem in regulationItems" :key="regulationItem.title" @click="navigateTo(regulationItem.to)">
          <template v-slot:prepend>
            <svg-icon type="mdi" :path="regulationItem.icon"></svg-icon>
          </template>
          <v-list-item-title class="ml-2">{{ regulationItem.title }}</v-list-item-title>
        </v-list-item>

        <v-divider></v-divider>
        <h5 class="mt-2" style="font-weight: 300;">{{ t('settings') }}</h5>

        <v-list-item v-for="settingItem in settingItems" :key="settingItem.title" @click="navigateTo(settingItem.to)">
          <template v-slot:prepend>
            <svg-icon type="mdi" :path="settingItem.icon"></svg-icon>
          </template>
          <v-list-item-title class="ml-2">{{ settingItem.title }}</v-list-item-title>
        </v-list-item>
      </v-list>
      <!-- Floating Button to Toggle Drawer -->
    <v-fab-transition>
      <v-btn 
        @click="drawer = !drawer" 
        fab 
        dark 
        class="drawer-toggle-btn"
      >
        <v-icon>{{ drawer ? 'mdi-chevron-double-left' : 'mdi-chevron-double-right' }}</v-icon>
      </v-btn>
    </v-fab-transition>
    </v-navigation-drawer>

    <v-main>
      <router-view></router-view>
    </v-main>

    
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useTheme } from 'vuetify';
import { useStore } from 'vuex';
import SvgIcon from '@jamescoyle/vue-icon';
import { mdiAccountMultiple, mdiInformationVariantCircle, mdiShapePlusOutline, mdiAccountPlus, mdiAccountStar, mdiLibraryShelves, mdiThemeLightDark, mdiCogs, mdiPlusMinusVariant, mdiViewDashboard, mdiPackage, mdiSwapHorizontal, mdiFileChartOutline, mdiWarehouse   } from '@mdi/js';
import logoDark from '@/assets/logo-dark.png'; 
import logoLight from '@/assets/logo-light.png';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const router = useRouter();
const drawer = ref(true);
const theme = useTheme();
const isDark = ref(false);
const store = useStore();

const user = localStorage.getItem('username');
const companyName = localStorage.getItem('companyname');
const apptitle = t("appname");

// Computed property for logo based on theme
const logoSrc = computed(() => {
  return isDark.value ? logoDark : logoLight;
});

// Check local storage for theme preference on mount
onMounted(() => {
const savedTheme = localStorage.getItem('theme');
  if (savedTheme) {
    theme.global.name.value = savedTheme;
    isDark.value = (savedTheme === 'dark');
  }
});

// Function to navigate to the settings page
const navigateToSettings = () => {
  router.push('/settings');
};

// Function to toggle theme between light and dark
const toggleTheme = () => {
  isDark.value = !isDark.value;
  const newTheme = isDark.value ? 'dark' : 'light';
  theme.global.name.value = newTheme;
  localStorage.setItem('theme', newTheme);
};

// Compute user initials based on the full name
const userInitials = computed(() => {
  const realname = localStorage.getItem('fullname') || '';
  return realname
    .split(' ')
    .map(name => name.charAt(0))
    .join('');
});

// Array of navigation items for the drawer
const items = [
  { title: t("products"), icon: mdiPackage, to: "/products" },
  { title: t("categories"), icon: mdiShapePlusOutline, to: "/categories" },
  { title: t("locations"), icon: mdiWarehouse, to: "/locations" },
  { title: t("customers"), icon: mdiAccountStar, to: "/customers" },
  { title: t("suppliers"), icon: mdiAccountPlus, to: "/suppliers" },
];

const inventoryItems = [
  { title: t("sales"), icon: mdiPackage, to: "/products" },
  { title: t("purchases"), icon: mdiLibraryShelves ,to: "/locations" },
];

const regulationItems = [
  { title: t("regulations"), icon: mdiPlusMinusVariant, to: "/regulations" },
  { title: t("stockMovements"), icon: mdiSwapHorizontal, to: "/regulations" },
  { title: t("reports"), icon: mdiFileChartOutline, to: "/reports" },
];

const settingItems = [
  { title: t("users"), icon: mdiAccountMultiple, to: "/users" },
  { title: t("about"), icon: mdiInformationVariantCircle, to: "/about" },
  { title: t("settings"), icon: mdiCogs, to: "/settings" },
];

// Function to navigate to given path
const navigateTo = (path) => {
  router.push(path);
};

// Handle logout functionality
const handleLogout = () => {
  console.log('handleLogout method called');
  store.dispatch('auth/logout') 
  .then(() => {
    router.push({ name: 'login' });
  })
  .catch((error) => {
    console.error('Error logging out:', error);
  });
};
</script>

<style scoped>
.title .text-center {
  font-size: 18px;
  font-weight: 500;
  height: 63px
}

.dark-link {
  color: #888888;
}

.light-link {
  color: #333333;
}

.theme-change {
  color:#FAFAFA;
  background: #1F3A3B;
  width: 2.2em; 
  height: 2.2em; 
  margin-right: 0.75em;
}

.logo-src {
  width: 160px;
}

.drawer {
  width: 240px !important;
  border-radius: 0 0 40px 0 !important;
}

.drawer-toggle-btn {
  position: fixed;
  bottom: 36px;
  right: -32px;
  background-color: #E4ECE2 !important;
  font-size: 2em;
  color: #333333 !important;
  border-radius: 50%;
  width: 36px;
  height: 68px;
}
</style>